import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    CAlert,
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CCol,
    CForm,
    CFormInput,
    CFormLabel,
    CFormSelect,
    CModal,
    CModalBody,
    CModalFooter,
    CModalHeader,
    CModalTitle,
    CRow,
    CSpinner,
    CTable,
    CTableBody,
    CTableDataCell,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilPencil, cilPlus, cilTrash } from '@coreui/icons';

import { apiClient } from 'services/Api';
import ToastNotification from 'components/globals/ToastNotification';

const currentYear = () => new Date().getFullYear();

const buildYearOptions = () => {
    const start = 2019;
    const end = currentYear() + 1;
    const years = [];
    for (let y = end; y >= start; y -= 1) years.push(y);
    return years;
};

const emptyForm = {
    year: currentYear(),
    indicator: '',
    weight: '',
};

const PT_INT = new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 0 });
const PT_NUM = new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 2 });
const PT_CURRENCY = new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
});

const RESULT_CURRENCY_KEYS = new Set([
    'mp04_montante_si_inov',
    'mp04_moldes',
    'mp04_aeronautica',
    'mp04_metalomecanica',
    'mp04_pedra_natural',
    'mp04_vidro',
    'mp04_textil',
    'mp04_turismo',
    'mp04_madeiras',
    'mp04_outros',
]);

const RESULT_PERCENT_KEYS = new Set([
    'mp01_objectives',
    'mp01_eficacia_acoes',
    'mp02_eficacia_formacao',
    'mp02_satisfacao_colabs',
    'mp03_satisfacao_clientes',
    'mp04_projetos_aprovados',
    'mp04_investimento_aprovado',
    'mp05_desvio',
    'mp05_desvio_horas',
    'mp06_ideias',
    'mp06_cumprimento_vigilancias',
    'mp06_concretizacao_idi',
    'mp06_desvio_idi',
]);

const RESULT_CURRENCY_HINTS = [
    '€',
    'eur',
    'montante',
    'volume de negócios',
    'faturação',
    'ebitda',
    'custos',
    'custo',
    'orçamento',
];

const RESULT_PERCENT_HINTS = ['%', 'taxa', 'grau', 'cumprimento', 'concretização', 'percent'];

const GROUPS = [
    { value: 'MP-01', label: 'Planeamento e Avaliação do Sistema de Gestão (MP-01)' },
    { value: 'MP-02', label: 'Gestão de Processos de Suporte (MP-02)' },
    { value: 'MP-03', label: 'Gestão de Comercial (MP-03)' },
    { value: 'MP-04', label: 'Elaboração e Gestão de Projetos com Incentivos (MP-04)' },
    { value: 'MP-05', label: 'Elaboração e Gestão de Projetos sem Incentivos (MP-05)' },
    { value: 'MP-06', label: 'Gestão da Inovação (MP-06)' },
];

function formatNumber(value) {
    if (value === null || value === undefined || value === '') return '—';
    const num = Number(value);
    if (Number.isNaN(num)) return '—';
    return Number.isInteger(num) ? num.toString() : PT_NUM.format(num);
}

function formatPercent(value) {
    const formatted = formatNumber(value);
    return formatted === '—' ? '—' : `${formatted}%`;
}

function parseNumberSafe(value) {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
}

function resolveResultType(row) {
    const key = (row?.indicator_key || '').toLowerCase();
    const desc = (row?.indicator_name || '').toLowerCase();

    if (desc.includes('%') || RESULT_PERCENT_KEYS.has(key) || RESULT_PERCENT_HINTS.some((h) => desc.includes(h))) {
        return 'percentage';
    }
    if (desc.includes('€') || RESULT_CURRENCY_KEYS.has(key) || RESULT_CURRENCY_HINTS.some((h) => desc.includes(h))) {
        return 'currency';
    }
    return 'raw';
}

function formatIndicatorValue(row, value) {
    const num = parseNumberSafe(value);
    if (num === null) return '—';

    const type = row?.indicator_value_type || resolveResultType(row);
    if (type === 'currency') return PT_CURRENCY.format(num);
    if (type === 'percentage') return `${PT_NUM.format(num)}%`;

    const formatter = Number.isInteger(num) ? PT_INT : PT_NUM;
    return formatter.format(num);
}

function parseWeight(value) {
    if (value === null || value === undefined || value === '') return null;
    const normalized = String(value).replace(',', '.');
    const num = Number(normalized);
    return Number.isFinite(num) ? num : null;
}

export default function FunctionalObjectivesPage() {
    const [toast, setToast] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    const [year, setYear] = useState(currentYear());
    const [rows, setRows] = useState([]);
    const [permissions, setPermissions] = useState({
        can_create: false,
        can_update: false,
        can_delete: false,
    });
    const [indicators, setIndicators] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ ...emptyForm });
    const [groupFilter, setGroupFilter] = useState('');

    const [deleteTarget, setDeleteTarget] = useState(null);

    const yearOptions = useMemo(() => buildYearOptions(), []);

    const totalWeight = useMemo(() => {
        return rows.reduce((acc, row) => {
            const num = Number(row.weight);
            return acc + (Number.isFinite(num) ? num : 0);
        }, 0);
    }, [rows]);

    const filteredIndicators = useMemo(() => {
        if (!groupFilter) return indicators;
        return indicators.filter((ind) => ind.group === groupFilter);
    }, [indicators, groupFilter]);

    const loadFunctionalObjectives = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiClient.get(`/functional-objectives/?year=${year}`);
            const data = response.data || {};
            setRows(Array.isArray(data.functional_objectives) ? data.functional_objectives : []);
            setPermissions(data.permissions || {});
        } catch (error) {
            setRows([]);
            setPermissions({ can_create: false, can_update: false, can_delete: false });
            setToast({
                title: 'Objetivos funcionais',
                message: 'Erro ao carregar objetivos funcionais.',
                type: 'error',
            });
        } finally {
            setLoading(false);
        }
    }, [year]);

    const loadIndicators = useCallback(async () => {
        try {
            const response = await apiClient.get(`/indicators/?year=${year}`);
            const data = response.data || {};
            const list = Array.isArray(data.indicators) ? data.indicators : [];
            list.sort((a, b) => (a.description || '').localeCompare(b.description || ''));
            setIndicators(list);
        } catch (error) {
            setIndicators([]);
        }
    }, [year]);

    useEffect(() => {
        loadFunctionalObjectives();
        loadIndicators();
    }, [loadFunctionalObjectives, loadIndicators]);

    const openCreate = () => {
        setEditing(null);
        setGroupFilter('');
        setForm({
            year,
            indicator: '',
            weight: '',
        });
        setModalOpen(true);
    };

    const openEdit = (row) => {
        const selectedIndicator = indicators.find((ind) => ind.id === row.indicator);
        setGroupFilter(selectedIndicator?.group || '');
        setEditing(row);
        setForm({
            year: row.year,
            indicator: row.indicator,
            weight: row.weight ?? '',
        });
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditing(null);
        setGroupFilter('');
        setForm({ ...emptyForm, year });
    };

    const handleGroupChange = (value) => {
        setGroupFilter(value);
        setForm((prev) => ({
            ...prev,
            indicator: '',
        }));
    };

    const submitForm = async (e) => {
        if (e?.preventDefault) e.preventDefault();

        const indicatorId = Number(form.indicator);
        if (!indicatorId) {
            setToast({
                title: 'Objetivos funcionais',
                message: 'Seleciona um indicador válido.',
                type: 'error',
            });
            return;
        }

        const weight = parseWeight(form.weight);
        if (weight === null) {
            setToast({
                title: 'Objetivos funcionais',
                message: 'Indica um peso válido.',
                type: 'error',
            });
            return;
        }

        const payload = {
            year: Number(form.year),
            indicator: indicatorId,
            weight,
        };

        setActionLoading(true);
        try {
            if (editing?.id) {
                await apiClient.put(`/functional-objectives/${editing.id}/`, payload);
            } else {
                await apiClient.post('/functional-objectives/', payload);
            }

            await loadFunctionalObjectives();
            closeModal();
        } catch (error) {
            setToast({
                title: 'Objetivos funcionais',
                message: 'Não foi possível guardar o objetivo funcional.',
                type: 'error',
            });
        } finally {
            setActionLoading(false);
        }
    };

    const deleteRow = async (id) => {
        setActionLoading(true);
        try {
            await apiClient.delete(`/functional-objectives/${id}/`);
            await loadFunctionalObjectives();
            setDeleteTarget(null);
        } catch (error) {
            setToast({
                title: 'Objetivos funcionais',
                message: 'Erro ao apagar o objetivo funcional.',
                type: 'error',
            });
        } finally {
            setActionLoading(false);
        }
    };

    const canCreate = !!permissions?.can_create;
    const canUpdate = !!permissions?.can_update;
    const canDelete = !!permissions?.can_delete;

    return (
        <>
            <ToastNotification toast={toast} setToast={setToast} />

            <CCard className="mb-4">
                <CCardHeader className="d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">Objetivos Funcionais</h4>
                    {canCreate && (
                        <CButton color="success" onClick={openCreate} disabled={actionLoading}>
                            <CIcon icon={cilPlus} className="me-2" />
                            Novo objetivo
                        </CButton>
                    )}
                </CCardHeader>

                <CCardBody>
                    <CRow className="g-3 mb-3">
                        <CCol md={4}>
                            <CFormLabel>Ano</CFormLabel>
                            <CFormSelect value={year} onChange={(e) => setYear(Number(e.target.value))}>
                                {yearOptions.map((y) => (
                                    <option key={y} value={y}>
                                        {y}
                                    </option>
                                ))}
                            </CFormSelect>
                        </CCol>
                        <CCol md={8} className="d-flex align-items-end">
                            <div>
                                <div className="fw-semibold">Total de pesos</div>
                                <div className="text-muted">{formatPercent(totalWeight)}</div>
                            </div>
                        </CCol>
                    </CRow>

                    {totalWeight < 100 && rows.length > 0 && (
                        <CAlert color="warning" className="mb-3">
                            A soma dos pesos deste ano está abaixo de 100%.
                        </CAlert>
                    )}

                    {loading ? (
                        <div className="d-flex align-items-center gap-2">
                            <CSpinner size="sm" /> A carregar…
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <CTable hover align="middle">
                                <CTableHead>
                                    <CTableRow>
                                        <CTableHeaderCell>Indicador</CTableHeaderCell>
                                        <CTableHeaderCell className="text-end">Objetivo</CTableHeaderCell>
                                        <CTableHeaderCell className="text-end">Obtido</CTableHeaderCell>
                                        <CTableHeaderCell className="text-end">Peso (%)</CTableHeaderCell>
                                        <CTableHeaderCell className="text-center">Score</CTableHeaderCell>
                                        {(canUpdate || canDelete) && <CTableHeaderCell style={{ width: 120 }} />}
                                    </CTableRow>
                                </CTableHead>
                                <CTableBody>
                                    {rows.length === 0 ? (
                                        <CTableRow>
                                            <CTableDataCell colSpan={canUpdate || canDelete ? 6 : 5} className="text-body-secondary">
                                                Sem dados
                                            </CTableDataCell>
                                        </CTableRow>
                                    ) : (
                                        rows.map((row) => (
                                            <CTableRow key={row.id}>
                                                <CTableDataCell>{row.indicator_name || '—'}</CTableDataCell>
                                                <CTableDataCell className="text-end">
                                                    {formatIndicatorValue(row, row.indicator_objective)}
                                                </CTableDataCell>
                                                <CTableDataCell className="text-end">
                                                    {formatIndicatorValue(row, row.indicator_obtained)}
                                                </CTableDataCell>
                                                <CTableDataCell className="text-end">
                                                    {formatPercent(row.weight)}
                                                </CTableDataCell>
                                                <CTableDataCell className="text-center">{row.score ?? '—'}</CTableDataCell>
                                                {(canUpdate || canDelete) && (
                                                    <CTableDataCell className="text-end">
                                                        <div className="d-flex justify-content-end gap-2">
                                                            {canUpdate && (
                                                                <CButton
                                                                    size="sm"
                                                                    color="secondary"
                                                                    variant="outline"
                                                                    onClick={() => openEdit(row)}
                                                                    disabled={actionLoading}
                                                                >
                                                                    <CIcon icon={cilPencil} />
                                                                </CButton>
                                                            )}
                                                            {canDelete && (
                                                                <CButton
                                                                    size="sm"
                                                                    color="danger"
                                                                    variant="outline"
                                                                    onClick={() => setDeleteTarget(row)}
                                                                    disabled={actionLoading}
                                                                >
                                                                    <CIcon icon={cilTrash} />
                                                                </CButton>
                                                            )}
                                                        </div>
                                                    </CTableDataCell>
                                                )}
                                            </CTableRow>
                                        ))
                                    )}
                                </CTableBody>
                            </CTable>
                        </div>
                    )}
                </CCardBody>
            </CCard>

            <CModal visible={modalOpen} onClose={closeModal} backdrop="static" alignment="center">
                <CModalHeader>
                    <CModalTitle>{editing ? 'Editar objetivo funcional' : 'Novo objetivo funcional'}</CModalTitle>
                </CModalHeader>
                <CForm onSubmit={submitForm}>
                    <CModalBody>
                        <CRow className="g-3">
                            <CCol md={4}>
                                <CFormLabel>Ano</CFormLabel>
                                <CFormInput value={form.year} readOnly />
                            </CCol>
                            <CCol md={8}>
                                <CFormLabel>Grupo</CFormLabel>
                                <CFormSelect
                                    value={groupFilter}
                                    onChange={(e) => handleGroupChange(e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    {GROUPS.map((group) => (
                                        <option key={group.value} value={group.value}>
                                            {group.label}
                                        </option>
                                    ))}
                                </CFormSelect>
                            </CCol>
                            <CCol md={8}>
                                <CFormLabel>Indicador</CFormLabel>
                                <CFormSelect
                                    value={form.indicator}
                                    onChange={(e) => setForm((prev) => ({ ...prev, indicator: e.target.value }))}
                                >
                                    <option value="">Seleciona…</option>
                                    {filteredIndicators.map((ind) => (
                                        <option key={ind.id} value={ind.id}>
                                            {ind.description}
                                        </option>
                                    ))}
                                </CFormSelect>
                            </CCol>
                            <CCol md={4}>
                                <CFormLabel>Peso (%)</CFormLabel>
                                <CFormInput
                                    value={form.weight}
                                    onChange={(e) => setForm((prev) => ({ ...prev, weight: e.target.value }))}
                                    placeholder="Ex: 25"
                                />
                            </CCol>
                        </CRow>
                    </CModalBody>
                    <CModalFooter>
                        <CButton color="secondary" variant="outline" onClick={closeModal} disabled={actionLoading}>
                            Cancelar
                        </CButton>
                        <CButton color="success" type="submit" disabled={actionLoading}>
                            {actionLoading ? <CSpinner size="sm" className="me-2" /> : null}
                            Guardar
                        </CButton>
                    </CModalFooter>
                </CForm>
            </CModal>

            <CModal visible={!!deleteTarget} onClose={() => setDeleteTarget(null)} alignment="center" backdrop="static">
                <CModalHeader>
                    <CModalTitle>Apagar objetivo funcional</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    {deleteTarget ? (
                        <>
                            <p className="mb-2">Tens a certeza que queres apagar este objetivo funcional?</p>
                            <div className="small text-body-secondary">Ano: {deleteTarget.year}</div>
                            <div className="fw-semibold">{deleteTarget.indicator_name || '—'}</div>
                        </>
                    ) : null}
                </CModalBody>
                <CModalFooter>
                    <CButton color="secondary" variant="outline" onClick={() => setDeleteTarget(null)} disabled={actionLoading}>
                        Cancelar
                    </CButton>
                    <CButton
                        color="danger"
                        onClick={() => deleteRow(deleteTarget.id)}
                        disabled={actionLoading || !deleteTarget?.id}
                    >
                        {actionLoading ? <CSpinner size="sm" className="me-2" /> : <CIcon icon={cilTrash} className="me-2" />}
                        Apagar
                    </CButton>
                </CModalFooter>
            </CModal>
        </>
    );
}
